import './ImageFiltersForm.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../../../../design/buttons/ButtonWithLoader';
import { S_Modal } from '../../../../../../../design/modals/modal/S_Modal';
import { ConfirmationModal } from '../../../../../../components/ConfirmationModal';
import { SaveFiltersContainer } from '../../containers/filters/SaveFilters.container';
import { FilterTabType, IAnnotationToolFilter, IImageFilters, IImageFiltersData, ISavedFilter, ISorting } from '../../imageFilters.model';
import { ImageAnnotationStatus, ImageSetType } from '../../projectDetailsImages.model';
import { AnnotatedByBody } from './annotatedBy/AnnotatedByBody';
import { AnnotatedByHeader } from './annotatedBy/AnnotatedByHeader';
import { AnnotationToolsBody } from './annotationTools/AnnotationToolsBody';
import { AnnotationToolsHeader } from './annotationTools/AnnotationToolsHeader';
import { CorrectedBody } from './corrected/CorrectedBody';
import { CorrectedHeader } from './corrected/CorrectedHeader';
import { ImageSetBody } from './imageSet/ImageSetBody';
import { ImageSetHeader } from './imageSet/ImageSetHeader';
import { InDatasetsBody } from './inDatasets/InDatasetsBody';
import { InDatasetsHeader } from './inDatasets/InDatasetsHeader';
import { ReviewedByBody } from './reviewdBy/ReviewedByBody';
import { ReviewedByHeader } from './reviewdBy/ReviewedByHeader';
import { SortingBody } from './sorting/SortingBody';
import { SortingHeader } from './sorting/SortingHeader';
import { StatusBody } from './status/StatusBody';
import { StatusHeader } from './status/StatusHeader';

export interface ImageFiltersFormProps {
  isClearAllEnabled: boolean;
  activeFiltersCount: number;
  showClearAllConfirmation: boolean;
  activeFilterType: FilterTabType;
  imageFilters: IImageFilters;
  availableFilters: IImageFiltersData;
  isLoading: boolean;
  savedFilters: ISavedFilter[];
  onFilterTabSelect: (filterType: FilterTabType) => void;
  onSetCorrected(corrected: boolean[]): void;
  onSetImageSets(imageSets: ImageSetType[]): void;
  onSetStatuses(annotationStatuses: ImageAnnotationStatus[]): void;
  onSetInDatasets: (datasets: string[]) => void;
  onSetReviewedBy: (reviewedBy: string[]) => void;
  onSetAnnotatedBy: (annotatedBy: string[]) => void;
  onSetAnnotationTools: (annotationTools: IAnnotationToolFilter[]) => void;
  onSetSorting(sorting: ISorting[]): void;
  onClearAll: () => void;
  onClearAllDeclined: () => void;
  onSavedFilterLoad: (savedFilter: ISavedFilter) => void;
  onClearAllConfirmedAsync: () => Promise<void>;
  onSavedFilterAsync: (name: string) => Promise<void>;
  onFilterDeleteAsync: (id: string) => Promise<void>;
  onApplyAsync: () => Promise<void>;
  onModalClose: () => void;
  onModalOpen: () => void;
  isModalOpen: boolean;
}

export const ImageFiltersForm = observer((props: ImageFiltersFormProps): ReactElement => {
  const { t } = useTranslator();

  const handleFilterSaveTabSelected = (): void => {
    props.onFilterTabSelect(FilterTabType.FilterSave);
  };

  const renderBody = (): ReactElement => {
    switch (props.activeFilterType) {
      case FilterTabType.InDatasets:
        return <InDatasetsBody {...props} />;
      case FilterTabType.AnnotatedBy:
        return <AnnotatedByBody {...props} />;
      case FilterTabType.ReviewedBy:
        return <ReviewedByBody {...props} />;
      case FilterTabType.Status:
        return <StatusBody {...props} />;
      case FilterTabType.AnnotationTools:
        return <AnnotationToolsBody {...props} />;
      case FilterTabType.Corrected:
        return <CorrectedBody {...props} />;
      case FilterTabType.ImageSet:
        return <ImageSetBody {...props} />;
      case FilterTabType.Sorting:
        return <SortingBody {...props} />;
      case FilterTabType.FilterSave:
        return (
          <SaveFiltersContainer
            onSavedFilterLoad={props.onSavedFilterLoad}
            onSaveFilterAsync={props.onSavedFilterAsync}
            onFilterDeleteAsync={props.onFilterDeleteAsync}
            savedFilters={props.savedFilters}
          />
        );
      default:
        throw 'invalid tab';
    }
  };

  return (
    <>
      <S_Modal
        onOpened={props.onModalOpen}
        className='image-filters-modal'
        isOpen={props.isModalOpen}
        toggle={props.onModalClose}
        headerText={t('filters_and_sorting')}
        buttons={
          <>
            <div className='left'>
              <Button
                onClick={handleFilterSaveTabSelected}
                disabled={props.isLoading}
              >
                {t('save_load_filters')}
              </Button>
            </div>
            <Button
              variant='outlined'
              color='neutral'
              onClick={props.onModalClose}
              disabled={props.isLoading}
            >
              {t('close')}
            </Button>
            <Button
              variant='outlined'
              onClick={props.onClearAll}
              disabled={props.isLoading || !props.isClearAllEnabled}
            >
              {t('clear_all')}
            </Button>
            <ButtonWithLoader
              onClick={props.onApplyAsync}
              loading={props.isLoading}
            >
              {t('apply')}
            </ButtonWithLoader>
          </>
        }
      >
        <div className='image-filters-container'>
          <div className='image-filters-headers'>
            <InDatasetsHeader {...props} />
            <AnnotatedByHeader {...props} />
            <ReviewedByHeader {...props} />
            <StatusHeader {...props} />
            <AnnotationToolsHeader {...props} />
            <CorrectedHeader {...props} />
            <ImageSetHeader {...props} />
            <SortingHeader {...props} />
          </div>
          <div className='image-filters-body'>{renderBody()}</div>
        </div>
      </S_Modal>
      <ConfirmationModal
        confirmationHeader={'clear_all'}
        confirmationDescription={'are_you_sure_you_want_to_delete_filters'}
        showModal={props.showClearAllConfirmation}
        onCancel={props.onClearAllDeclined}
        onConfirm={props.onClearAllConfirmedAsync}
        confirmationYes={'clear'}
      />
    </>
  );
});
